/**
 * rosSystems.js — WHICH BODY SYSTEM A QUESTION IS ABOUT. ROS-KEYWORD-MATCH-1.
 *
 * ★★★ THE DEFECT THIS ENDS. The review of systems was built by running the
 *   PATIENT'S ANSWER TEXT through English keyword lists with `includes()`.
 *   `includes()` is a substring test, so "No chest pain" contains "chest pain",
 *   and a DENIAL surfaced as a POSITIVE. Executed against the live module on
 *   2026-09-07, unchanged since it was filed:
 *
 *       buildReportedSystems(['No chest pain'], 'ENT')
 *         -> Respiratory  positive  ["No chest pain"]
 *            Cardiovascular positive ["No chest pain"]
 *            Musculoskeletal positive ["No chest pain"]
 *
 *   One denial, three fabricated positives, in a clinical document. And
 *   `buildReportedSystems(['Sin dolor de pecho'], 'ENT')` matched nothing at all,
 *   because the 73 keywords are English.
 *
 * ★★★ THE FIX IS NOT A BETTER MATCHER. No amount of negation handling survives
 *   translation, and every keyword list is a new thing to keep in eleven
 *   languages. THE SYSTEM IS A PROPERTY OF THE QUESTION, WHICH IS AUTHORED ONCE
 *   AND HAS AN ID — not a property of the answer, which is free text in whatever
 *   language the patient speaks. This file is that property. The answer decides
 *   only WHETHER the question was answered; it never decides what it was about.
 *
 * ★★ WHY THE TAG IS HERE AND NOT IN questionnaires.json. The DB is the source of
 *   truth for the catalog (useQuestionnaireStore: the admin panel publishes it,
 *   the bundled JSON is an offline fallback), so a tag added to the shipped JSON
 *   would not reach a live case. A tag here ships with the code that renders the
 *   note. THE COST IS NAMED: a question added through the admin panel that this
 *   file does not know resolves to null and is ABSENT from the ROS. That is the
 *   safe direction — absent is not a claim — and it is the same invariant
 *   reviewOfSystems.js already holds. check-ros-negatives asserts coverage of the
 *   SHIPPED catalog; it cannot see DB-only questions, and nothing here pretends
 *   otherwise.
 */

/**
 * ★★★ THE MODEL, DR. RAMI 2026-09-08, AND IT REPLACES THE ONE BELOW IT.
 *
 *   "Any given specialty can include questions that could easily be needed to be
 *    asked in another specialty. The question is not defined by the specialty.
 *    Orella just needs the specialty frame to work through the case."
 *
 * So a question is ONE OF TWO KINDS, and they behave oppositely:
 *
 *   SELF-DEFINING — it means something on its own and the frame is IGNORED.
 *     "Any dizziness or balance problems?" is vestibular whether the patient
 *     reached it through Ear, Brain & Nerves, or a general complaint.
 *   FRAME-DEPENDENT — it means nothing on its own and inheriting the frame is
 *     CORRECT, not a compromise. "How severe is it? (1-10)" has no body system;
 *     it is only interpretable inside the question that precedes it.
 *
 * ★★★ THE FIRST VERSION OF THIS FILE TREATED EVERY QUESTION AS THE SECOND KIND,
 *   which let the frame leak into the clinical record: "Any dizziness or balance
 *   problems?" exists only in the Ear questionnaire, so a patient reporting
 *   dizziness COULD NEVER PRODUCE A NEUROLOGICAL LINE. The note said HEENT
 *   because of the door they walked through, not because of what they said.
 *
 * ★★ AND THE CATALOG SHOWS BOTH KINDS PLAINLY. Measured 2026-09-08: 393
 *   signs_symptoms questions, only 236 distinct texts, and 20 of those texts
 *   appear under MORE THAN ONE specialty — "How severe is it? (1-10)" under
 *   nine, "Are any of these happening right now?" under seventeen. A question
 *   that appears under seventeen specialties has no system of its own; that is
 *   what frame-dependent looks like from the data.
 *
 * ★ RESOLUTION ORDER: question → focus area → specialty. A self-defining
 *   question whose system already equals its frame's needs no row, because the
 *   result is identical — rows exist where the two DISAGREE, which is exactly
 *   where the leak was.
 */

/** THE FRAME. Used for frame-dependent questions, which is most of them.
 *  Dr. Rami's ruling, 2026-09-07. `null` means the specialty asserts no system. */
export const SPECIALTY_SYSTEM = {
  ear: 'HEENT',
  eye: 'HEENT',
  nose: 'HEENT',
  throat: 'HEENT',
  'head-neck': 'HEENT',
  teeth: 'HEENT',
  skin: 'Skin',
  'plastic-surgery': 'Skin',
  'stomach-gut': 'Gastrointestinal',
  urinary: 'Genitourinary',
  kidney: 'Genitourinary',
  'womens-health': 'Genitourinary',
  'muscle-joints': 'Musculoskeletal',
  feet: 'Musculoskeletal',
  // ★ HIS RULING: Chronic Pain is Musculoskeletal. Most of its focus areas are
  //   back, joint and muscle; the headache area is a candidate override below.
  pain: 'Musculoskeletal',
  heart: 'Cardiovascular',
  vascular: 'Cardiovascular',
  lungs: 'Respiratory',
  'brain-nerves': 'Neurological',
  'mental-health': 'Psychiatric',
  sleep: 'Psychiatric',
  'health-wellness': 'General',
  blood: 'General',
  infection: 'General',
  anesthesiology: 'General',
  // ★★ HIS RULING: a General case has no body system BY CONSTRUCTION — the
  //    patient chose "General health concern" precisely because they could not
  //    name one. It contributes NOTHING to the ROS rather than a manufactured
  //    heading. `null`, not absent from this table: the difference is that a
  //    specialty missing from the table is an unknown, and an unknown is also
  //    null, but only this one is null ON PURPOSE.
  general: null,
}

/**
 * SELF-DEFINING FOCUS AREAS — every question in the area is about the area, so
 * the area is what defines them and the specialty is ignored.
 *
 * ★ These three are the ones where the whole area departs from its specialty and
 *   the clinical call is not in doubt. `ear/dizziness` is vestibular, not otologic
 *   — its severity and frequency questions are dizziness questions, which is the
 *   model working: a frame-dependent question inside a self-defining area
 *   correctly inherits the AREA.
 * ★★ AREAS WHERE IT IS A JUDGMENT CALL ARE DELIBERATELY ABSENT AND LISTED HERE
 *    RATHER THAN GUESSED: pain/chest-pain (chest wall vs cardiac vs pleuritic),
 *    health-wellness/sleep (the ten ROS systems have no Sleep), and
 *    stomach-gut/weight-loss (constitutional vs GI). Each inherits until ruled.
 */
export const AREA_SYSTEM = {
  'ear/dizziness': 'Neurological',
  'pain/headache': 'Neurological',
  'throat/cough': 'Respiratory',
  // ★★★ HIS RULING, 2026-09-08, AND IT IS THE SHARPEST ONE IN THE TABLE:
  //   "chest wall, cardiac, or pleuritic? thats what we need to figure out (lol) —
  //    this by itself is General."
  //   An undifferentiated chest pain is the case where naming a system IS the
  //   claim under investigation. Musculoskeletal (the Chronic Pain default) would
  //   have asserted the answer to the question the visit exists to settle. General
  //   asserts only that the patient reported something.
  //   ★★ AND ITS q5 STILL DEPARTS — see QUESTION_SYSTEM. The area says "we do not
  //      know"; one question inside it names Respiratory, because "is the pain
  //      related to breathing?" is pleuritic wherever it is asked. That is the
  //      two-kind model doing something neither level could do alone.
  'pain/chest-pain': 'General',
}

/**
 * SELF-DEFINING QUESTIONS — a single question inside an area that departs from it.
 *
 * ★★★ NOTHING GENERIC MAY APPEAR HERE, and check-ros-negatives enforces it: a
 *   text that occurs under three or more specialties is frame-dependent BY
 *   DEMONSTRATION, and tagging one self-defining would put "How severe is it?"
 *   under Cardiovascular because it once appeared in the heart questionnaire.
 * ★★★ RULED BY DR. RAMI, 2026-09-08, AND SEVEN OF THE ORIGINAL FOUR-PLUS-THREE
 *   ROWS IS WHERE FOURTEEN CANDIDATES LANDED. Of the 24 candidates, HE CONFIRMED SIX OF THE SEVEN I HAD
 *   MARKED WRONG — itchy eyes is ocular, nasal airway and odynophagia are ENT, the
 *   globus stress question is a modifier, nocturnal heartburn is GI — and those
 *   stay inheriting, with no row, because inheriting is already the right answer.
 *   He OVERRULED me on one: snoring goes to Respiratory. Of the seven I had no
 *   opinion on, four resolve to the frame they already had (the Wellness sleep
 *   questions are General; trouble starting a swallow is ENT) and need no row.
 *   ★★ THREE ROWS CAME OUT OF FOURTEEN CANDIDATES, AND THAT IS THE POINT — most
 *      questions genuinely are frame-dependent, and a table that grew to 24 rows
 *      would have been recording a keyword pass rather than a clinical judgment.
 *
 *   ★★★ AND THE TABLE IS STILL SMALL FOR THE ORIGINAL REASON: the 24 candidates
 *      came from a keyword pass over the QUESTION text — the same instrument this
 *      file removed from the ANSWER text — so every one needed a clinician before
 *      it could be called evidence, and most of them did not survive.
 */
export const QUESTION_SYSTEM = {
  'ear/hearing-loss/q4': 'Neurological',         // Any dizziness or balance problems?
  'muscle-joints/back-pain/q4': 'Neurological',  // numbness, tingling or weakness in your legs
  'muscle-joints/neck-pain/q5': 'Neurological',  // headaches associated with the neck pain
  'stomach-gut/weight-loss/q4': 'Psychiatric',   // Mood and mental health:
  // ── ruled by Dr. Rami, 2026-09-08, from the 14 that were left open ──────────
  'nose/congestion/q5': 'Respiratory',           // snoring / trouble breathing at night
  'pain/chest-pain/q5': 'Respiratory',           // is the pain related to breathing? (pleuritic)
  'stomach-gut/weight-loss/q1': 'General',       // what's happened to your appetite?
}

/**
 * The body system a question belongs to, or null when nothing here claims one.
 * ★ Resolution order is question → area → specialty, which is the two-kind model
 *   in one line: the most specific thing that DEFINES this question wins, and
 *   when nothing defines it, the frame is the answer rather than a fallback.
 * ★ null is a real answer, not a failure: it means this question contributes no
 *   system, and reviewOfSystems will leave that system ABSENT rather than
 *   asserting anything about it.
 */
export function systemForQuestion(specialtyId, focusAreaId, questionId) {
  const q = `${specialtyId}/${focusAreaId}/${questionId}`
  if (Object.prototype.hasOwnProperty.call(QUESTION_SYSTEM, q)) return QUESTION_SYSTEM[q]
  const a = `${specialtyId}/${focusAreaId}`
  if (Object.prototype.hasOwnProperty.call(AREA_SYSTEM, a)) return AREA_SYSTEM[a]
  if (Object.prototype.hasOwnProperty.call(SPECIALTY_SYSTEM, specialtyId)) return SPECIALTY_SYSTEM[specialtyId]
  return null
}
